/**
 * Tiny synthesised sound kit. No audio assets, no network cost — just
 * oscillators, which keeps the whole thing under a kilobyte and lets us
 * unlock the AudioContext from the first user gesture (required on iOS).
 */

type Cue = 'connect' | 'skip' | 'message' | 'spark' | 'search' | 'error' | 'stay' | 'knock';

const NOTES: Record<Cue, { freqs: number[]; type: OscillatorType; gain: number; step: number }> = {
  connect: { freqs: [523.25, 659.25, 783.99], type: 'sine', gain: 0.16, step: 0.07 },
  skip: { freqs: [392, 293.66], type: 'triangle', gain: 0.1, step: 0.06 },
  message: { freqs: [880], type: 'sine', gain: 0.09, step: 0.05 },
  spark: { freqs: [659.25, 880, 1174.66, 1567.98], type: 'sine', gain: 0.18, step: 0.075 },
  search: { freqs: [440, 554.37], type: 'sine', gain: 0.07, step: 0.05 },
  error: { freqs: [220, 185], type: 'sawtooth', gain: 0.08, step: 0.08 },
  stay: { freqs: [392, 523.25, 659.25, 783.99], type: 'triangle', gain: 0.15, step: 0.09 },
  knock: { freqs: [587.33, 587.33, 880], type: 'sine', gain: 0.14, step: 0.13 },
};

let ctx: AudioContext | null = null;
let enabled = true;

export function setSoundEnabled(value: boolean): void {
  enabled = value;
}

/** Must be called from a user gesture the first time, or iOS keeps audio muted. */
export function unlockAudio(): void {
  if (ctx) {
    void ctx.resume();
    return;
  }
  try {
    ctx = new AudioContext();
    void ctx.resume();
  } catch {
    ctx = null;
  }
}

export function play(cue: Cue): void {
  if (!enabled || !ctx || ctx.state === 'closed') return;
  if (ctx.state === 'suspended') void ctx.resume();

  const note = NOTES[cue];
  const master = ctx.createGain();
  master.gain.value = note.gain;
  master.connect(ctx.destination);

  note.freqs.forEach((freq, index) => {
    const osc = ctx!.createOscillator();
    const env = ctx!.createGain();
    const start = ctx!.currentTime + index * note.step;
    const end = start + 0.18;

    osc.type = note.type;
    osc.frequency.setValueAtTime(freq, start);
    env.gain.setValueAtTime(0.0001, start);
    env.gain.exponentialRampToValueAtTime(1, start + 0.012);
    env.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.connect(env).connect(master);
    osc.start(start);
    osc.stop(end + 0.02);
  });
}

/** Short haptic pulse where supported (Android Chrome, some desktop browsers). */
export function haptic(pattern: number | number[] = 12): void {
  if (!enabled) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* not supported */
  }
}
